import type { TemplateDefinition } from "./types";

export const T09: TemplateDefinition = {
  id: "t09",
  module: 3,
  number: 9,
  name: "Community Policing Strategy Document",
  stage: "STAGE 2 — COMPETENCY",
  pillars: "Value-Based Credentialing + Tiered Access",
  whyExists:
    "Enforcement-only approaches do not move the needle on safety or satisfaction. This strategy is how a certified officer designs and implements a community safety system — not just shows up for a patrol shift.",
  architectureContext:
    "This template serves the Value-Based Credentialing pillar. It transforms community policing from a slogan into a documented strategy with data-driven problem analysis, partnerships, and measurable outcomes.",
  sections: [
    {
      id: "s1",
      title: "Section 1 — District Problem Analysis",
      fields: [
        { id: "district", type: "text", label: "Patrol District", prefillFrom: "district" },
        { id: "analysis_date", type: "date", label: "Analysis Date" },
        { id: "data_sources", type: "textarea", label: "Data Sources Used", placeholder: "Crime stats, community surveys, calls-for-service, etc." },
      ],
      repeatable: {
        id: "safety_problems",
        label: "Safety Problem",
        maxRows: 3,
        initialRows: 3,
        addLabel: "+ Add Problem",
        fields: [
          { id: "title", type: "text", label: "Problem Title", placeholder: "e.g., Vehicle break-ins in Meridian Heights" },
          { id: "data", type: "textarea", label: "Data supporting this as a priority", placeholder: "Supporting data..." },
          { id: "geography", type: "text", label: "Geographic concentration", placeholder: "Where is this concentrated..." },
          { id: "population", type: "text", label: "Most affected population", placeholder: "Who is most affected..." },
        ],
      },
    },
    {
      id: "s2",
      title: "Section 2 — Root Cause Assessment",
      description: "For each of the 3 problems identified above:",
      repeatable: {
        id: "root_causes",
        label: "Root Cause Analysis",
        maxRows: 3,
        initialRows: 3,
        fields: [
          { id: "problem_ref", type: "text", label: "Problem Reference", placeholder: "Which problem (1, 2, or 3)" },
          { id: "underlying_causes", type: "textarea", label: "Underlying causes", placeholder: "Root causes driving this problem..." },
          { id: "community_factors", type: "textarea", label: "Community factors contributing", placeholder: "Community-level factors..." },
          { id: "le_factors", type: "textarea", label: "Law enforcement factors contributing", placeholder: "LE factors contributing..." },
        ],
      },
    },
    {
      id: "s3",
      title: "Section 3 — Partnership Identification",
      repeatable: {
        id: "partners",
        label: "Partner",
        maxRows: 10,
        initialRows: 1,
        addLabel: "+ Add Partner",
        fields: [
          { id: "org_name", type: "text", label: "Organization Name", placeholder: "Organization name..." },
          { id: "category", type: "select", label: "Category", options: [{ label: "Faith", value: "faith" }, { label: "Schools", value: "schools" }, { label: "Businesses", value: "businesses" }, { label: "Social Services", value: "social_services" }, { label: "Advocacy", value: "advocacy" }, { label: "Government", value: "government" }, { label: "Other", value: "other" }] },
          { id: "problem_addressed", type: "text", label: "Safety problem this partner can help address", placeholder: "Which problem..." },
          { id: "contact", type: "text", label: "Contact person", placeholder: "Contact name..." },
          { id: "relationship", type: "select", label: "Relationship status", options: [{ label: "No Contact", value: "no_contact" }, { label: "Aware", value: "aware" }, { label: "Engaged", value: "engaged" }, { label: "Active Partner", value: "active" }] },
        ],
      },
    },
    {
      id: "s4",
      title: "Section 4 — Officer Engagement Actions",
      repeatable: {
        id: "engagement_actions",
        label: "Engagement Action",
        maxRows: 12,
        initialRows: 1,
        addLabel: "+ Add Action",
        fields: [
          { id: "action", type: "text", label: "Action", placeholder: "What you will do..." },
          { id: "location", type: "text", label: "Location", placeholder: "Where..." },
          { id: "schedule", type: "text", label: "Schedule", placeholder: "When/how often..." },
          { id: "target_problem", type: "text", label: "Target problem addressed", placeholder: "Which problem..." },
          { id: "target_partner", type: "text", label: "Target partner involved", placeholder: "Which partner..." },
          { id: "status", type: "select", label: "Status", options: [{ label: "Planned", value: "planned" }, { label: "Active", value: "active" }, { label: "Complete", value: "complete" }] },
        ],
      },
    },
    {
      id: "s5",
      title: "Section 5 — Outcome Metrics",
      fields: [
        { id: "safety_metric", type: "text", label: "My primary safety outcome metric", placeholder: "e.g., Part 1 crime reduction %" },
        { id: "satisfaction_metric", type: "text", label: "My primary community satisfaction metric", placeholder: "e.g., Survey satisfaction score" },
        { id: "target_90", type: "textarea", label: "My 90-day measurable target", placeholder: "90-day target..." },
        { id: "data_collection", type: "textarea", label: "How I will collect and report this data", placeholder: "Your data collection approach..." },
        { id: "review_freq", type: "select", label: "Review frequency", options: [{ label: "Weekly", value: "weekly" }, { label: "Bi-weekly", value: "biweekly" }, { label: "Monthly", value: "monthly" }] },
      ],
    },
  ],
};

export const T10: TemplateDefinition = {
  id: "t10",
  module: 3,
  number: 10,
  name: "De-escalation Protocol and Response Guide",
  stage: "STAGE 2 — COMPETENCY",
  pillars: "Value-Based Credentialing + Tiered Access",
  whyExists:
    "Most officers know de-escalation matters. A certified officer has a documented, practiced protocol they follow — not just good intentions.",
  architectureContext:
    "This template serves the Value-Based Credentialing pillar. It converts de-escalation from aspiration into a documented, repeatable protocol with clear decision points and documentation requirements.",
  sections: [
    {
      id: "step1",
      title: "Step 1 — Initial Assessment (Read the Scene)",
      variant: "steps",
      fields: [
        { id: "assess_before", type: "textarea", label: "Before engaging, I assess", placeholder: "Threat level, subject's mental state, audience, environment, officer-to-subject positioning" },
        { id: "deesc_indicators", type: "textarea", label: "Indicators that tell me de-escalation is the appropriate primary approach", placeholder: "Indicators favoring de-escalation..." },
        { id: "limit_indicators", type: "textarea", label: "Indicators that tell me de-escalation has immediate limits", placeholder: "Indicators limiting de-escalation..." },
      ],
    },
    {
      id: "step2",
      title: "Step 2 — Communication Opening",
      variant: "steps",
      fields: [
        { id: "voice_tone", type: "text", label: "Voice tone and volume", placeholder: "Your approach to voice..." },
        { id: "positioning", type: "text", label: "Physical positioning", placeholder: "Your positioning approach..." },
        { id: "opening_language", type: "textarea", label: "Opening language", placeholder: "What you say to open contact..." },
        { id: "avoid_saying", type: "textarea", label: "What I avoid saying in the first 30 seconds", placeholder: "Language to avoid..." },
      ],
    },
    {
      id: "step3",
      title: "Step 3 — Active Listening and Validation",
      variant: "steps",
      fields: [
        { id: "listening_techniques", type: "textarea", label: "The active listening techniques I use to keep the subject engaged", placeholder: "Your listening techniques..." },
        { id: "validation", type: "textarea", label: "How I validate without agreeing with threatening behavior", placeholder: "Your validation approach..." },
        { id: "working_signs", type: "textarea", label: "Signs that active listening is working", placeholder: "Indicators listening is effective..." },
        { id: "shift_signs", type: "textarea", label: "Signs that I need to shift approach", placeholder: "Indicators you need to change approach..." },
      ],
    },
    {
      id: "step4",
      title: "Step 4 — Option Presentation",
      variant: "steps",
      fields: [
        { id: "face_saving", type: "textarea", label: "How I give the subject a face-saving path to compliance", placeholder: "Your approach to face-saving options..." },
        { id: "option_language", type: "textarea", label: "The specific language I use to present options", placeholder: "Specific phrases you use..." },
        { id: "avoid_ultimatums", type: "textarea", label: "How I avoid ultimatums that escalate rather than resolve", placeholder: "Your approach to avoiding ultimatums..." },
      ],
    },
    {
      id: "step5",
      title: "Step 5 — Decision Point and Transition Documentation",
      variant: "steps",
      fields: [
        { id: "limit_indicators", type: "textarea", label: "The specific indicators that tell me de-escalation has reached its limit", placeholder: "Indicators de-escalation is exhausted..." },
        { id: "transition_protocol", type: "textarea", label: "My transition protocol when moving from de-escalation to force options", placeholder: "Your transition protocol..." },
        { id: "doc_protocol", type: "textarea", label: "How I document the de-escalation attempt and decision point in my report", placeholder: "Your documentation approach..." },
        { id: "supervisor_notify", type: "text", label: "Supervisor notification requirement when de-escalation is unsuccessful", placeholder: "e.g., Notify Shift Sergeant" },
      ],
    },
  ],
};

export const T11: TemplateDefinition = {
  id: "t11",
  module: 3,
  number: 11,
  name: "Procedural Justice Implementation Plan",
  stage: "STAGE 2 — COMPETENCY",
  pillars: "Value-Based Credentialing + Tiered Access",
  whyExists:
    "Procedural justice reduces use-of-force incidents and complaints when implemented systematically. This plan embeds the four pillars into your daily operations.",
  architectureContext:
    "This template serves the Value-Based Credentialing pillar. It operationalizes the four pillars of procedural justice (Voice, Neutrality, Respect, Trustworthiness) as documented, measurable daily practices.",
  sections: [
    {
      id: "voice",
      title: "Pillar 1 — Voice",
      variant: "pillars",
      description: "Color badge: Blue",
      fields: [
        { id: "voice_meaning", type: "textarea", label: "What giving people a voice means operationally in my encounters", placeholder: "Your operational definition..." },
        { id: "voice_behaviors", type: "textarea", label: "Specific behaviors I demonstrate to give people voice", placeholder: "Specific behaviors..." },
        { id: "voice_doc", type: "textarea", label: "How I document voice-giving in my reports", placeholder: "Your documentation approach..." },
        { id: "voice_measure", type: "text", label: "How I measure this pillar", placeholder: "Your measurement approach..." },
      ],
    },
    {
      id: "neutrality",
      title: "Pillar 2 — Neutrality",
      variant: "pillars",
      description: "Color badge: Purple",
      fields: [
        { id: "neut_meaning", type: "textarea", label: "What neutrality means in my decision-making", placeholder: "Your definition of neutrality..." },
        { id: "neut_behaviors", type: "textarea", label: "How I demonstrate consistency and impartiality in the field", placeholder: "Your impartiality practices..." },
        { id: "neut_doc", type: "textarea", label: "How I document neutrality", placeholder: "Your documentation approach..." },
        { id: "neut_measure", type: "text", label: "How I measure this pillar", placeholder: "Your measurement approach..." },
      ],
    },
    {
      id: "respect",
      title: "Pillar 3 — Respect",
      variant: "pillars",
      description: "Color badge: Green",
      fields: [
        { id: "respect_meaning", type: "textarea", label: "What dignity and respect look like in my encounters regardless of the situation", placeholder: "Your definition of respect..." },
        { id: "respect_behaviors", type: "textarea", label: "Specific behaviors I commit to in every encounter", placeholder: "Your committed behaviors..." },
        { id: "respect_doc", type: "textarea", label: "How I document respectful treatment", placeholder: "Your documentation approach..." },
        { id: "respect_measure", type: "text", label: "How I measure this pillar", placeholder: "Your measurement approach..." },
      ],
    },
    {
      id: "trustworthiness",
      title: "Pillar 4 — Trustworthiness",
      variant: "pillars",
      description: "Color badge: Gold",
      fields: [
        { id: "trust_meaning", type: "textarea", label: "How I demonstrate that my motives are benevolent in the field", placeholder: "How you show benevolent motives..." },
        { id: "trust_explain", type: "textarea", label: "How I explain my actions to community members", placeholder: "Your explanation approach..." },
        { id: "trust_doc", type: "textarea", label: "How I document trustworthiness", placeholder: "Your documentation approach..." },
        { id: "trust_measure", type: "text", label: "How I measure this pillar", placeholder: "Your measurement approach..." },
      ],
    },
    {
      id: "schedule",
      title: "Implementation Schedule",
      fields: [
        { id: "plan_90", type: "textarea", label: "My 90-day implementation plan for embedding all four pillars", placeholder: "Your 90-day plan..." },
        { id: "feedback_mech", type: "text", label: "Community feedback mechanism I will use", placeholder: "e.g., Post-contact survey, community council feedback" },
      ],
    },
  ],
};

export const T12: TemplateDefinition = {
  id: "t12",
  module: 3,
  number: 12,
  name: "Community Partnership Map and 90-Day Engagement Plan",
  stage: "STAGE 2 — COMPETENCY",
  pillars: "Value-Based Credentialing + Tiered Access",
  whyExists:
    "A certified officer can name the 10 most important non-law-enforcement partners in their area, rate every relationship, and show a 90-day plan to strengthen them.",
  architectureContext:
    "This template serves the Tiered Access pillar. It maps your community partnership ecosystem and creates a structured 90-day plan to strengthen relationships — a professional competency beyond patrol operations.",
  sections: [
    {
      id: "s1",
      title: "Section 1 — Stakeholder Identification",
      repeatable: {
        id: "stakeholders",
        label: "Stakeholder",
        maxRows: 20,
        initialRows: 1,
        addLabel: "+ Add Stakeholder",
        fields: [
          { id: "org_name", type: "text", label: "Organization Name", placeholder: "Organization..." },
          { id: "category", type: "select", label: "Category", options: [{ label: "Faith", value: "faith" }, { label: "Schools", value: "schools" }, { label: "Businesses", value: "businesses" }, { label: "Social Services", value: "social_services" }, { label: "Advocacy", value: "advocacy" }, { label: "Government", value: "government" }] },
          { id: "key_contact", type: "text", label: "Key Contact", placeholder: "Contact name..." },
          { id: "contact_info", type: "text", label: "Phone / Email", placeholder: "Contact info..." },
          { id: "offer", type: "textarea", label: "What they offer to community safety", placeholder: "Their contribution..." },
        ],
      },
    },
    {
      id: "s2",
      title: "Section 2 — Relationship Assessment",
      repeatable: {
        id: "relationship_assess",
        label: "Relationship Assessment",
        maxRows: 20,
        initialRows: 1,
        fields: [
          { id: "partner_name", type: "text", label: "Partner Name", placeholder: "Partner from Section 1..." },
          { id: "level", type: "select", label: "Current relationship level", options: [{ label: "No Contact", value: "no_contact" }, { label: "Aware", value: "aware" }, { label: "Engaged", value: "engaged" }, { label: "Active Partner", value: "active" }] },
          { id: "last_contact", type: "date", label: "Last point of contact" },
          { id: "notes", type: "text", label: "Relationship notes", placeholder: "Notes..." },
        ],
      },
    },
    {
      id: "s3",
      title: "Section 3 — Partnership Value Analysis",
      fields: [
        { id: "top3_valuable", type: "textarea", label: "My top 3 most valuable current partnerships and why", placeholder: "Your most valuable partnerships..." },
        { id: "top3_gaps", type: "textarea", label: "My top 3 partnership gaps — relationships I need to build", placeholder: "Partnerships you need to build..." },
        { id: "most_impact", type: "text", label: "The partnership that would have the most impact on my top safety problem", placeholder: "Highest-impact partnership..." },
      ],
    },
    {
      id: "s4",
      title: "Section 4 — 90-Day Engagement Plan",
      variant: "timeline",
      repeatable: {
        id: "engagement_events",
        label: "Engagement Event",
        maxRows: 20,
        initialRows: 1,
        addLabel: "+ Add Event",
        fields: [
          { id: "partner", type: "text", label: "Partner name", placeholder: "Partner..." },
          { id: "type", type: "text", label: "Action/meeting type", placeholder: "e.g., Coffee meeting, community event" },
          { id: "date", type: "date", label: "Date" },
          { id: "objective", type: "text", label: "Objective", placeholder: "What you want to achieve..." },
          { id: "outcome", type: "textarea", label: "Outcome recorded after the fact", placeholder: "Record the outcome after the event..." },
        ],
      },
    },
  ],
};
